import { Container } from "@/components/Container.tsx";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState } from "react";
import { usePersonasQuery } from "@/api/personas";
import { AgentCard } from "@/components/manage-personas/AgentCard";
import { useAgentsPageTour } from "./useAgentsPageTour";
import CustomDialogEmpty from "./CustomDialogEmpty";
import { useWelcomeDialogState } from "./useWelcomeDialogState";
import { useAuth } from "@/hooks/use-auth.tsx";

export function AgentsPage() {
  const { authenticated } = useAuth();
  const {
    data: agents = [],
    isError,
    isPending,
  } = usePersonasQuery(authenticated);
  const { startTour, hasCompletedTour } = useAgentsPageTour();
  const shouldShowTour = !hasCompletedTour && authenticated;
  const [searchQuery, setSearchQuery] = useState("");
  const [emptyDialogOpen, onEmptyDialogClose] = useWelcomeDialogState();

  const filteredAgents = agents.filter(
    (agent) =>
      agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      agent.persona_id.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  if (!isPending && !emptyDialogOpen && shouldShowTour && authenticated) {
    startTour();
  }

  return (
    <Container className="flex flex-col gap-4">
      <div className="flex flex-col items-start">
        <div className="flex flex-col sm:flex-row gap-4 w-full mb-7">
          <Input
            placeholder="Search"
            className="w-80"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-agents-page-tour="search-input"
            autoFocus
          />

          <Button
            className="ml-auto"
            asChild
            data-agents-page-tour="create-agent-button"
          >
            <Link to="/agents/new">Create Agent</Link>
          </Button>
        </div>

        <div className="w-full">
          <div className="mx-auto">
            <div
              className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
              data-testid="personas-table-information"
            >
              {isPending ? (
                <div className="py-4 col-span-4 px-4 animate-pulse">
                  <div className="h-40 rounded-[2px] bg-gray-900/60" />
                </div>
              ) : isError ? (
                <div className="py-4 col-span-3 px-4 min-h-24 ">
                  <div className="w-full mb-2">Error loading agents</div>
                </div>
              ) : filteredAgents.length === 0 ? (
                <div className="py-4 col-span-3 px-4 min-h-24 ">
                  <div className="w-full mb-2">No agents found</div>
                </div>
              ) : (
                Object.values(filteredAgents).map((agent, index) => (
                  <AgentCard
                    key={agent.persona_id}
                    agent={agent}
                    index={index}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
      {!isPending && (
        <CustomDialogEmpty
          open={emptyDialogOpen}
          onClose={onEmptyDialogClose}
        />
      )}
    </Container>
  );
}
